/**
 * EnemySpawner - Handles spawning enemies in the world
 */

import { Vector3 } from '../math/Vector3';
import { Enemy, EnemyConfig, ENEMY_CONFIGS } from './Enemy';
import { Player } from '../entities/Player';
import { World } from '../world/World';

export class EnemySpawner {
  private world: World;
  private player: Player | null = null;
  private enemies: Enemy[] = [];

  private spawnTimer: number = 0;
  private spawnInterval: number = 4; // Seconds between spawn attempts
  private maxEnemies: number = 12;
  private spawnRadius: number = 12;
  private despawnRadius: number = 25;

  private isNight: boolean = false;

  // Callbacks
  public onEnemySpawned: ((enemy: Enemy) => void) | null = null;
  public onEnemyDeath: ((enemy: Enemy) => void) | null = null;

  // Movement check callback (set by Game to include structure checks)
  public enemyCanMoveTo: ((x: number, y: number) => boolean) | null = null;

  constructor(world: World) {
    this.world = world;
  }

  setPlayer(player: Player): void {
    this.player = player;
    // Spawn initial enemies
    this.spawnInitialEnemies(3);
  }

  private spawnInitialEnemies(count: number): void {
    for (let i = 0; i < count; i++) {
      this.trySpawn();
    }
  }

  setNight(isNight: boolean): void {
    this.isNight = isNight;
    // More enemies at night
    this.maxEnemies = isNight ? 20 : 12;
    this.spawnInterval = isNight ? 2 : 4;
  }

  update(deltaTime: number): void {
    if (!this.player) return;

    this.spawnTimer += deltaTime;

    // Try to spawn
    if (this.spawnTimer >= this.spawnInterval) {
      this.spawnTimer = 0;
      this.trySpawn();
    }

    // Update enemies
    for (const enemy of this.enemies) {
      enemy.update(deltaTime);

      // Set player as target if in range
      if (!enemy.canSeeTarget() && enemy.distanceToPoint(this.player.position) <= enemy.config.detectionRange) {
        enemy.setTarget(this.player);
      }
    }

    // Despawn far enemies and remove dead ones
    this.enemies = this.enemies.filter(enemy => {
      if (!enemy.isActive) {
        return false;
      }

      const dist = enemy.distanceToPoint(this.player!.position);
      if (dist > this.despawnRadius) {
        enemy.destroy();
        return false;
      }

      return true;
    });
  }

  private trySpawn(): void {
    if (!this.player) return;

    if (this.enemies.length >= this.maxEnemies) return;

    // Find spawn position
    const spawnPos = this.findSpawnPosition();
    if (!spawnPos) return;

    // Choose enemy type
    const config = this.chooseEnemyType();

    // Spawn enemy
    const enemy = new Enemy(spawnPos, config);
    enemy.movement.canMoveTo = (x, y) => {
      if (this.enemyCanMoveTo) {
        return this.enemyCanMoveTo(x, y);
      }
      return this.world.isWalkable(x, y);
    };
    enemy.onDeath = (e) => {
      this.onEnemyDeath?.(e);
    };

    this.enemies.push(enemy);
    this.world.addEntity(enemy);
    this.onEnemySpawned?.(enemy);
  }

  private findSpawnPosition(): Vector3 | null {
    if (!this.player) return null;

    // Try several random positions
    for (let attempts = 0; attempts < 10; attempts++) {
      const angle = Math.random() * Math.PI * 2;
      const distance = this.spawnRadius + Math.random() * 5;

      const x = this.player.position.x + Math.cos(angle) * distance;
      const y = this.player.position.y + Math.sin(angle) * distance;

      if (this.world.isWalkable(x, y)) {
        return new Vector3(x, y, 0);
      }
    }

    return null;
  }

  private chooseEnemyType(): EnemyConfig {
    // Weights for: zombie, skeleton, spider, wolf, orc, golem, demon, ghost
    const dayWeights =   [0.25, 0.15, 0.20, 0.20, 0.10, 0.05, 0.02, 0.03];
    const nightWeights = [0.15, 0.20, 0.10, 0.15, 0.12, 0.08, 0.08, 0.12];

    const types = Object.keys(ENEMY_CONFIGS);
    const weights = this.isNight ? nightWeights : dayWeights;

    let roll = Math.random();
    for (let i = 0; i < types.length; i++) {
      const weight = weights[i] ?? 0;
      roll -= weight;
      if (roll <= 0) {
        const key = types[i];
        if (key && ENEMY_CONFIGS[key]) {
          return ENEMY_CONFIGS[key];
        }
      }
    }

    return ENEMY_CONFIGS.zombie!;
  }

  getEnemies(): Enemy[] {
    return this.enemies;
  }

  getEnemyCount(): number {
    return this.enemies.length;
  }

  clearAllEnemies(): void {
    for (const enemy of this.enemies) {
      enemy.destroy();
    }
    this.enemies = [];
  }
}
